import { BREAKDOWN_TYPE } from "../recipeImpactChart.constants";
import {
  BreakdownItem,
  ChartSourceData,
  ChartStyleItem,
  ImpactBreakdown,
  RecipeChartStylePayload
} from "../recipeImpactChart.types";
import {
  buildPatternIndexById,
  collectBreakdownItems,
  collectBreakdownItemsInDiscoveryOrder,
  indexRecipeDefinitionById,
  indexSourceItemsById,
  mergeSourceIndexes,
  resolveColorById
} from "../utils/breakdownItemCollection.utill";
import { enrichBreakdownWithChartStyle } from "../utils/breakdownEnrichment.utill";
import { getValidVariantBreakdowns } from "./recipeImpactChart.variants.helper";

/** Builds legend items with resolved colors and process pattern indices. */
const buildLegendItems = (
  items: BreakdownItem[],
  colorById: Record<string, string>,
  patternIndexById: Record<string, number>
): ChartStyleItem[] =>
  items.map(item => ({
    id: item.id,
    name: item.name,
    type: item.type,
    chartColor: colorById[item.id],
    ...(item.type === BREAKDOWN_TYPE.process && patternIndexById[item.id] !== undefined
      ? { patternIndex: patternIndexById[item.id] }
      : {})
  }));

/**
 * Resolves chart styling using the same logic as the simulator ECharts builder
 * and returns enriched breakdowns + legend for the report PDF download payload.
 */
export const buildRecipeChartStylePayload = (source: ChartSourceData): RecipeChartStylePayload => {
  const { recipeResults, originalRecipeData } = source;
  const originalBreakdown = recipeResults?.originalRecipeImpactBreakdown || {};

  const allBreakdowns = [
    originalBreakdown,
    ...(recipeResults?.variantstotalImpacts || []).map(
      variant => variant.variantImpactBreakdown || {}
    )
  ];

  const discoveryOrderItems = collectBreakdownItemsInDiscoveryOrder(allBreakdowns);
  const breakdownItems = collectBreakdownItems([
    originalBreakdown,
    ...getValidVariantBreakdowns(recipeResults, originalRecipeData)
  ]);

  const colorById = resolveColorById(
    breakdownItems,
    mergeSourceIndexes(
      indexSourceItemsById(allBreakdowns),
      indexRecipeDefinitionById(originalRecipeData?.originalRecipe, originalRecipeData?.variants)
    ),
    discoveryOrderItems
  );
  const patternIndexById = buildPatternIndexById(discoveryOrderItems);
  const legendItems = buildLegendItems(breakdownItems, colorById, patternIndexById);

  const enrichedOriginalBreakdown = enrichBreakdownWithChartStyle(
    originalBreakdown,
    colorById,
    patternIndexById
  );

  const enrichedVariantBreakdowns: Record<string, ImpactBreakdown> = {};
  (recipeResults?.variantstotalImpacts || []).forEach(variant => {
    if (variant._id && variant.variantImpactBreakdown) {
      enrichedVariantBreakdowns[variant._id] = enrichBreakdownWithChartStyle(
        variant.variantImpactBreakdown,
        colorById,
        patternIndexById
      );
    }
  });

  return {
    legendItems,
    enrichedOriginalBreakdown,
    enrichedVariantBreakdowns
  };
};
